import {
  faBolt,
  faBrain,
  faCloud,
  faHeadSideCoughSlash,
  faHeartPulse,
  faScaleBalanced,
  faShieldHeart,
  faUtensils
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Col, Row, Card, App, Modal, Button, Typography } from 'antd'
import React, { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { handleError } from '../../../../lib/utils'
import DesktopMedicalHistoryNav from './Nav'
import { updateMentalHealth } from '../../api/api'
import patMedHistReducer from '../../utils/reducer'
import '../style/DesktopMentalHealth.css'
import DesktopSectionHeader from './DesktopSectionHeader'

const MENTAL_HEALTH_OPTIONS = [
  {
    value: 'Depression',
    label: 'Depression',
    icon: faCloud,
    color: '239, 68, 68'
  },
  {
    value: 'Anxiety',
    label: 'Anxiety',
    icon: faBolt,
    color: '168, 85, 247'
  },
  {
    value: 'Bipolar',
    label: 'Bipolar',
    icon: faScaleBalanced,
    color: '236, 72, 153'
  },
  {
    value: 'PTSD',
    label: 'PTSD',
    icon: faShieldHeart,
    color: '251, 146, 60'
  },
  {
    value: 'ADHD',
    label: 'ADHD',
    icon: faHeartPulse,
    color: '16, 178, 225'
  },
  {
    value: 'Eating Disorder',
    label: 'Eating Disorder',
    icon: faUtensils,
    color: '139, 92, 246'
  },
  {
    value: 'OCD',
    label: 'OCD',
    icon: faHeadSideCoughSlash,
    color: '81, 207, 102'
  }
]

const DesktopMentalHealth = ({ setFacet, setSaving }) => {
  const { message } = App.useApp()
  const patMedHist = useSelector((state) => state.patMedHist.data) || {}
  const mentalHealth = patMedHist.mentalHealth || {}
  const [selectedOptions, setSelectedOptions] = useState(mentalHealth.list || [])
  const dispatch = useDispatch()

  // Unsaved changes modal state
  const [showUnsavedModal, setShowUnsavedModal] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState(null)
  const initialState = useRef(null)

  // Capture initial state when component loads - with delay to ensure state is loaded
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!initialState.current && selectedOptions) {
        initialState.current = [...selectedOptions]
        console.log('Initial mental health state captured:', initialState.current)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [selectedOptions])

  // Clear initial state when component unmounts
  useEffect(() => {
    return () => {
      initialState.current = null
    }
  }, [])

  // Scroll to top when component mounts
  useEffect(() => {
    setTimeout(() => {
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
    }, 0)
  }, [])

  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    if (!initialState.current) return false

    const hasChanges = JSON.stringify(selectedOptions) !== JSON.stringify(initialState.current)
    console.log('Mental health change detection:', {
      hasChanges,
      initial: initialState.current,
      current: selectedOptions
    })

    return hasChanges
  }

  // Handle navigation with unsaved changes check
  const handleNavigation = (navigationFunction) => {
    if (hasUnsavedChanges()) {
      setPendingNavigation(() => navigationFunction)
      setShowUnsavedModal(true)
    } else {
      navigationFunction()
    }
  }

  // Update initial state after successful save
  const updateInitialStateAfterSave = () => {
    initialState.current = [...selectedOptions]
    console.log('Initial state updated after save:', initialState.current)
  }

  // Wrapper for handleSubmit that also updates initial state
  const handleSaveWithStateUpdate = async () => {
    try {
      await handleSubmit()
      updateInitialStateAfterSave()
    } catch (error) {
      // Error is already handled in handleSubmit
    }
  }

  // Handle saving and then navigating
  const handleSaveAndNavigate = async () => {
    try {
      await handleSubmit()
      updateInitialStateAfterSave()
      setShowUnsavedModal(false)
      if (pendingNavigation) {
        pendingNavigation()
        setPendingNavigation(null)
      }
    } catch (error) {
      // Error is already handled in handleSubmit
    }
  }

  // Handle navigation without saving
  const handleNavigateWithoutSaving = () => {
    setShowUnsavedModal(false)
    if (pendingNavigation) {
      pendingNavigation()
      setPendingNavigation(null)
    }
  }

  const handleOptionClick = (optionValue) => {
    setSelectedOptions((prevSelected) =>
      prevSelected.includes(optionValue)
        ? prevSelected.filter((value) => value !== optionValue)
        : [...prevSelected, optionValue]
    )
  }

  const handleSubmit = async () => {
    // Check if we have a valid medical history record
    if (!patMedHist._id) {
      message.error('Medical history record not found. Please refresh the page and try again.')
      return
    }

    setSaving(true)
    try {
      const newRecord = await updateMentalHealth(patMedHist._id, selectedOptions)
      dispatch(patMedHistReducer.actions.updateState(newRecord))
      setFacet(null)
    } catch (error) {
      message.error(handleError(error))
    } finally {
      setSaving(false)
    }
  }

  const renderMentalHealthOptions = () => {
    return (
      <Row gutter={[20, 20]}>
        {MENTAL_HEALTH_OPTIONS.map((option) => {
          const isChecked = selectedOptions.includes(option.value)

          return (
            <Col xs={24} sm={12} md={8} lg={6} key={option.value}>
              <div
                className={`desktop-mental-health-option ${isChecked ? 'selected' : ''}`}
                onClick={() => handleOptionClick(option.value)}
                role='checkbox'
                aria-checked={isChecked}
                tabIndex={0}
                style={{
                  '--option-color': option.color
                }}
              >
                <div className='desktop-mental-health-icon'>
                  <FontAwesomeIcon icon={option.icon} />
                </div>
                <span className='desktop-mental-health-label'>{option.label}</span>
              </div>
            </Col>
          )
        })}
      </Row>
    )
  }

  return (
    <div className='desktop-mental-health-container'>
      <DesktopSectionHeader
        icon={faBrain}
        title='Mental Health'
        subtitle='Please select any conditions you have experienced'
        gradient='linear-gradient(135deg, #5C6BC0 0%, #7986CB 100%)'
      />

      <div className='desktop-mental-health-content'>
        <Card className='desktop-mental-health-card'>{renderMentalHealthOptions()}</Card>
      </div>

      <DesktopMedicalHistoryNav
        prevFunction={() => handleNavigation(() => setFacet(null))}
        nextFunction={handleSaveWithStateUpdate}
        prevText='Back to Overview'
        nextText='Save Mental Health'
      />

      {/* Unsaved Changes Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FontAwesomeIcon icon={faBrain} style={{ color: '#ff6b6b' }} />
            <span>Unsaved Changes</span>
          </div>
        }
        open={showUnsavedModal}
        onCancel={() => setShowUnsavedModal(false)}
        footer={null}
        className='desktop-unsaved-changes-modal'
        centered
      >
        <div className='desktop-unsaved-changes-content'>
          <Typography.Text className='desktop-unsaved-changes-message'>
            You have unsaved changes to your mental health information. What would you like to do?
          </Typography.Text>

          <div className='desktop-unsaved-changes-actions'>
            <Button type='primary' className='desktop-save-changes-btn' onClick={handleSaveAndNavigate} loading={false}>
              Save Changes
            </Button>
            <Button type='default' className='desktop-discard-changes-btn' onClick={handleNavigateWithoutSaving}>
              Discard Changes
            </Button>
            <Button type='text' className='desktop-cancel-modal-btn' onClick={() => setShowUnsavedModal(false)}>
              Continue Editing
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default DesktopMentalHealth
